import React, { useEffect, useState, useCallback } from 'react';
import { RefreshCw, Copy, Download, Eye, Check, KeyRound } from 'lucide-react';
import { adminApi } from '../api';
import { useAuth } from '../AuthContext';

const Section = ({ title, children, testid }) => (
  <div className="rounded-2xl border border-paelbi-line bg-white p-6" data-testid={testid}>
    <h3 className="font-display text-lg text-paelbi-ink">{title}</h3>
    <div className="mt-4 space-y-4">{children}</div>
  </div>
);

export function GuestAccess() {
  const { tenantId } = useAuth();
  const [ga, setGa] = useState(null);
  const [bust, setBust] = useState(Date.now());
  const [pin, setPin] = useState('');
  const [msg, setMsg] = useState('');
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (tenantId) adminApi.getGuestAccess(tenantId).then((d) => { setGa(d); setPin(d.guest_pin || ''); setBust(Date.now()); });
  }, [tenantId]);
  useEffect(() => { load(); }, [load]);

  if (!ga) return <p className="text-sm text-paelbi-ink/50">Caricamento accesso ospiti...</p>;

  const copyLink = async () => {
    await navigator.clipboard.writeText(ga.guest_url);
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };

  const savePin = async () => {
    if (!/^\d{4}$/.test(pin)) { setMsg('Il PIN deve essere di 4 cifre'); return; }
    setBusy(true); setMsg('');
    try { const d = await adminApi.updateGuestPin(tenantId, pin); setGa(d); setMsg('PIN aggiornato'); }
    catch (e) { setMsg('Errore: ' + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const regen = async () => {
    if (!window.confirm('Rigenerare il QR? I QR stampati in precedenza smetteranno di funzionare. Il PIN resta invariato.')) return;
    setBusy(true); setMsg('');
    try { const d = await adminApi.regenerateGuestToken(tenantId); setGa(d); setBust(Date.now()); setMsg('Nuovo QR generato'); }
    catch (e) { setMsg('Errore: ' + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  const openPreview = () => window.open(`${ga.guest_url}?preview=1`, '_blank', 'noopener');

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl text-paelbi-ink">Accesso ospiti</h1>
          <p className="mt-1 text-paelbi-ink/60">QR privato e PIN per far accedere i tuoi ospiti alla pagina della struttura.</p>
        </div>
        <button type="button" onClick={openPreview} data-testid="guest-preview-button"
          className="inline-flex items-center gap-2 rounded-btn bg-paelbi-brown px-5 py-2.5 text-sm font-semibold text-white hover:bg-paelbi-brown-dark">
          <Eye size={16} /> Anteprima pagina
        </button>
      </div>

      <div className="mt-6 grid gap-6 md:grid-cols-2">
        <Section title="QR Code" testid="guest-qr-section">
          <div className="flex flex-col items-center gap-3">
            <img src={`${ga.qr_png}?v=${bust}`} alt="QR accesso ospiti" data-testid="guest-qr-img"
              className="h-52 w-52 rounded-lg border border-paelbi-line" />
            <p className="text-xs text-paelbi-ink/50">Il QR contiene solo il token e apre la pagina ospiti.</p>
            <div className="flex flex-wrap justify-center gap-2">
              <a href={`${ga.qr_png}?v=${bust}`} download data-testid="guest-download-png"
                className="inline-flex items-center gap-1.5 rounded-btn border border-paelbi-line px-3 py-2 text-sm text-paelbi-ink/80 hover:border-paelbi-brown/50"><Download size={15} /> PNG</a>
              <a href={ga.qr_pdf} target="_blank" rel="noopener noreferrer" data-testid="guest-download-pdf"
                className="inline-flex items-center gap-1.5 rounded-btn border border-paelbi-line px-3 py-2 text-sm text-paelbi-ink/80 hover:border-paelbi-brown/50"><Download size={15} /> PDF</a>
              <button type="button" onClick={regen} disabled={busy} data-testid="guest-regen-button"
                className="inline-flex items-center gap-1.5 rounded-btn border border-paelbi-line px-3 py-2 text-sm text-paelbi-ink/80 hover:border-paelbi-brown/50"><RefreshCw size={15} /> Rigenera QR</button>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-paelbi-ink/80">Link diretto</label>
            <div className="flex gap-2">
              <input readOnly value={ga.guest_url} data-testid="guest-link-input"
                className="w-full rounded-lg border border-paelbi-line bg-paelbi-bg px-3 py-2 text-xs text-paelbi-ink/70" />
              <button type="button" onClick={copyLink} data-testid="guest-copy-link"
                className="inline-flex shrink-0 items-center gap-1.5 rounded-btn border border-paelbi-line px-3 text-sm text-paelbi-ink/80 hover:border-paelbi-brown/50">
                {copied ? <Check size={15} className="text-green-600" /> : <Copy size={15} />}{copied ? 'Copiato' : 'Copia'}
              </button>
            </div>
          </div>
        </Section>

        <Section title="PIN di accesso" testid="guest-pin-section">
          <div className="flex items-center gap-2 text-paelbi-ink/70"><KeyRound size={18} className="text-paelbi-brown" />
            <span className="text-sm">Comunica questo PIN ai tuoi ospiti.</span></div>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-paelbi-ink/80">PIN (4 cifre)</span>
            <input inputMode="numeric" maxLength={4} value={pin} data-testid="guest-pin-input-admin"
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              className="w-40 rounded-lg border border-paelbi-line bg-white px-3 py-2 text-center text-2xl tracking-[0.4em] outline-none focus:border-paelbi-brown" />
          </label>
          <button type="button" onClick={savePin} disabled={busy} data-testid="guest-save-pin"
            className="rounded-btn bg-paelbi-brown px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60">
            {busy ? 'Salvo...' : 'Salva PIN'}
          </button>
          {msg && <p className={`text-sm ${msg.startsWith('Errore') ? 'text-red-600' : 'text-green-700'}`} data-testid="guest-msg">{msg}</p>}
        </Section>
      </div>
    </div>
  );
}

export default GuestAccess;
